import React from "react";
import { FiLogOut, FiChevronLeft, FiX } from "react-icons/fi";
import { MENU_GROUPS } from "./menuConfig";

/**
 * Fixed, collapsible navigation. Desktop = rail that collapses to icons;
 * mobile = off-canvas drawer (controlled by parent).
 *
 * Pure presentation: selection + logout are delegated upward.
 */
export default function Sidebar({
    activeTab,
    onSelect,
    collapsed,
    onToggleCollapse,
    mobileOpen,
    onCloseMobile,
    onLogout,
    badges = {},
}) {
    return (
        <>
            {/* Mobile overlay */}
            <div
                className={`fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm transition-opacity lg:hidden ${
                    mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
                }`}
                onClick={onCloseMobile}
                aria-hidden="true"
            />

            <aside
                className={`fixed inset-y-0 left-0 z-50 flex flex-col border-r border-slate-200/80 bg-white transition-all duration-250
                    dark:border-slate-800 dark:bg-slate-900
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
                    lg:translate-x-0 ${collapsed ? "lg:w-[78px]" : "lg:w-64"} w-64`}
            >
                {/* Brand */}
                <div className={`flex h-16 flex-shrink-0 items-center border-b border-slate-100 dark:border-slate-800 ${collapsed ? "lg:justify-center lg:px-0" : ""} px-4`}>
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 text-sm font-bold text-white shadow-soft">
                            XF
                        </div>
                        <div className={`min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
                            <p className="truncate text-sm font-bold text-slate-900 dark:text-white">XF Admin</p>
                            <p className="truncate text-[11px] text-slate-400 dark:text-slate-500">Administration Panel</p>
                        </div>
                    </div>
                    <button
                        onClick={onCloseMobile}
                        className="ml-auto rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 lg:hidden"
                        aria-label="Tutup menu"
                    >
                        <FiX className="h-5 w-5" />
                    </button>
                </div>

                {/* Nav */}
                <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 scrollbar-slim">
                    {MENU_GROUPS.map((group, gi) => (
                        <div key={group.id} className={gi > 0 ? "mt-6" : ""}>
                            {group.label && (
                                <p className={`mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 ${collapsed ? "lg:hidden" : ""}`}>
                                    {group.label}
                                </p>
                            )}
                            <div className="space-y-1">
                                {group.items.map((item) => {
                                    const Icon = item.icon;
                                    const active = activeTab === item.id;
                                    const badge = badges[item.id];
                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => onSelect(item.id)}
                                            title={collapsed ? item.label : undefined}
                                            aria-current={active ? "page" : undefined}
                                            className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150
                                                ${active
                                                    ? "bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300"
                                                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                                                }
                                                ${collapsed ? "lg:justify-center lg:px-0" : ""}`}
                                        >
                                            {/* active accent */}
                                            <span
                                                className={`absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-primary-500 transition-opacity ${active ? "opacity-100" : "opacity-0"}`}
                                            />
                                            <Icon className={`h-5 w-5 flex-shrink-0 ${active ? "text-primary-600 dark:text-primary-400" : ""}`} />
                                            <span className={`flex-1 truncate text-left ${collapsed ? "lg:hidden" : ""}`}>{item.label}</span>
                                            {badge > 0 && (
                                                <span className={`flex h-5 min-w-[20px] items-center justify-center rounded-full bg-danger-500 px-1.5 text-[10px] font-bold text-white ${collapsed ? "lg:absolute lg:right-2 lg:top-2" : ""}`}>
                                                    {badge > 99 ? "99+" : badge}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* Footer: collapse + logout */}
                <div className="flex-shrink-0 border-t border-slate-100 p-3 dark:border-slate-800">
                    <button
                        onClick={onToggleCollapse}
                        className={`mb-1 hidden w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200 lg:flex ${collapsed ? "lg:justify-center lg:px-0" : ""}`}
                        aria-label={collapsed ? "Perluas sidebar" : "Ciutkan sidebar"}
                    >
                        <FiChevronLeft className={`h-5 w-5 transition-transform duration-250 ${collapsed ? "rotate-180" : ""}`} />
                        <span className={collapsed ? "lg:hidden" : ""}>Ciutkan</span>
                    </button>
                    <button
                        onClick={onLogout}
                        className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-danger-600 transition-colors hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-500/10 ${collapsed ? "lg:justify-center lg:px-0" : ""}`}
                    >
                        <FiLogOut className="h-5 w-5 flex-shrink-0" />
                        <span className={collapsed ? "lg:hidden" : ""}>Logout</span>
                    </button>
                </div>
            </aside>
        </>
    );
}
